/**
 * Configuración de rutas con React Router DOM v7. Define rutas para login, dashboard, consulta, histórico, campañas, configuración, usuarios y más.
 */
import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "views/login/Login";
import PrivateRoute from "components/routes/PrivateRoute";
import { useAuth } from "AuthContext";
import NotFound from "views/notFound/NotFound";
import RecuperarContrasenia from "views/recuperarContrasenia/RecuperarContrasenia";
import SpinOneFIN from "components/spinOneFIN/SpinOneFIN";

const Dashboard = lazy(() => import("components/metabase/Dashboard"));
const EstadosDelDia = lazy(() => import("components/metabase/EstadosDelDia"));
const Usuarios = lazy(() => import("views/usuarios/Usuarios"));
const Notificaciones = lazy(() => import("views/notificaciones/Notificaciones"));
const Campañas = lazy(() => import("views/campañas/Campañas"));
const Historico = lazy(() => import("views/historico/Historico"));
const Consulta = lazy(() => import("views/consulta/Consulta"));
const Configuracion = lazy(() => import("views/configuracion/Configuracion"));
const ImpactoConsultaVenta = lazy(() => import("components/metabase/ImpactoConsultaVenta"));

const AppRoutes = ({ loggedUser, decodedUser }) => {
  const { menuItemsRol } = useAuth();

  const permisosVista = new Set((menuItemsRol || []).map((p) => p.vista));
  const puedeVer = (nombre) => permisosVista.has(nombre);

  const isAuth = loggedUser && Object.keys(loggedUser).length > 0;

  // Redirección después de login basada en permisos
  const getRedirectPath = () => {
    if (
      ["true", "True", true].includes(decodedUser?.habilitaDashboard) &&
      puedeVer("Panel de Control")
    ) {
      return "/panel-control";
    } else if (puedeVer("Consulta")) {
      return "/consulta";
    }
    return "/not-found";
  };

  return (
    <Suspense
      fallback={
        <div className="loading-container">
          <SpinOneFIN />
        </div>
      }
    >
      <Routes>
      {/* ---------- PUBLIC ---------- */}
      <Route path="/" element={isAuth ? <Navigate to={getRedirectPath()} replace /> : <Login />} />

      <Route path="/recuperar" element={<RecuperarContrasenia />} />

      {/* ---------- PRIVATE ---------- */}

      <Route
        path="/panel-control"
        element={
          <PrivateRoute
            element={<Dashboard />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Panel de Control")}
          />
        }
      />

      <Route
        path="/consulta"
        element={
          <PrivateRoute
            element={<Consulta />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Consulta")}
          />
        }
      />

      <Route
        path="/estados-del-dia"
        element={
          <PrivateRoute
            element={<EstadosDelDia />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Estados del Día")}
          />
        }
      />

      <Route
        path="/historico"
        element={
          <PrivateRoute
            element={<Historico />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Histórico de Consultas")}
          />
        }
      />

      <Route
        path="/impacto-consulta-ventas"
        element={
          <PrivateRoute
            element={<ImpactoConsultaVenta />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Impacto de la Consulta en Ventas")}
          />
        }
      />

      <Route
        path="/usuarios"
        element={
          <PrivateRoute
            element={<Usuarios />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Usuarios")}
          />
        }
      />

      <Route
        path="/configuracion/*"
        element={
          <PrivateRoute
            element={<Configuracion />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Configuración")}
          />
        }
      />

      <Route
        path="/notificaciones"
        element={
          <PrivateRoute
            element={<Notificaciones />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Notificaciones")}
          />
        }
      />

      <Route
        path="/campanias"
        element={
          <PrivateRoute
            element={<Campañas />}
            loggedUser={loggedUser}
            requiredPermission={puedeVer("Campañas")}
          />
        }
      />

      {/* ---------- FALLBACK ---------- */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;
